import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Faq from './Faq';
import HelpTooltip from '../components/HelpTooltip';
import CancelSurveyModal from '../components/CancelSurveyModal';

const LOGO = '/dailyreps3.png';

export default function ClubAdmin() {
  const navigate = useNavigate();
  const { user, logout, apiFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('teams');
  const [dashboard, setDashboard] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCancelSurvey, setShowCancelSurvey] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showMovePlayer, setShowMovePlayer] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Forms
  const [teamForm, setTeamForm] = useState({ name: '', age_group: '', has_under_13: false, coach_ids: [] });
  const [moveForm, setMoveForm] = useState({ player_id: '', destination_team_id: '' });

  // Inline coach invite (per-team)
  const [inviteForTeamId, setInviteForTeamId] = useState(null);
  const [inlineInviteEmail, setInlineInviteEmail] = useState('');

  // Inline invite players (per-team on team list)
  const [invitePlayersForTeamId, setInvitePlayersForTeamId] = useState(null);
  const [invitePlayersCopyMsg, setInvitePlayersCopyMsg] = useState('');

  // Add player from team list (Club Pay only)
  const [addPlayerForTeamId, setAddPlayerForTeamId] = useState(null);
  const [addPlayerListForm, setAddPlayerListForm] = useState({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });

  // View Players (roster drilldown)
  const [viewingTeamId, setViewingTeamId] = useState(null);
  const [viewingTeamPlayers, setViewingTeamPlayers] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [addPlayerForm, setAddPlayerForm] = useState({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
  const [showInvitePlayers, setShowInvitePlayers] = useState(false);
  const [inviteCopyMsg, setInviteCopyMsg] = useState('');

  // View Coaches (coach drilldown per team)
  const [viewingCoachesTeamId, setViewingCoachesTeamId] = useState(null);
  const [viewingTeamCoaches, setViewingTeamCoaches] = useState([]);
  const [viewingTeamInvitations, setViewingTeamInvitations] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [editingCoachId, setEditingCoachId] = useState(null);
  const [editCoachForm, setEditCoachForm] = useState({ first_name: '', last_name: '', email: '' });

  // Import
  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Reports
  const [clubReports, setClubReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);

  // Billing
  const [billingData, setBillingData] = useState(null);
  const [billingPlans, setBillingPlans] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Players for move (loaded when a team is selected)
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [moveSourceTeam, setMoveSourceTeam] = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      const data = await apiFetch('/api/club/dashboard');
      setDashboard(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiFetch]);

  const loadCoaches = useCallback(async () => {
    try {
      const data = await apiFetch('/api/club/coaches');
      setCoaches(data.coaches);
    } catch (err) { console.error(err); }
  }, [apiFetch]);

  const loadClubReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const data = await apiFetch('/api/club/reports');
      setClubReports(data);
    } catch (err) { console.error(err); }
    finally { setReportsLoading(false); }
  }, [apiFetch]);

  const loadBilling = useCallback(async () => {
    setBillingLoading(true);
    try {
      const [subData, planData] = await Promise.all([
        apiFetch('/api/billing/subscription'),
        apiFetch('/api/billing/plans'),
      ]);
      setBillingData(subData);
      setBillingPlans(planData);
    } catch (err) { console.error(err); }
    finally { setBillingLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadDashboard(); loadCoaches(); }, [loadDashboard, loadCoaches]);

  useEffect(() => {
    if (activeTab === 'billing') loadBilling();
  }, [activeTab, loadBilling]);

  const loadTeamPlayers = async (teamId) => {
    setMoveSourceTeam(teamId);
    try {
      const data = await apiFetch('/api/admin/players', {
        headers: { 'x-team-id': teamId },
      });
      setTeamPlayers(data.players);
    } catch (err) { console.error(err); }
  };

  // Load roster for View Players drilldown
  const loadRoster = async (teamId) => {
    setRosterLoading(true);
    try {
      const data = await apiFetch('/api/admin/players', {
        headers: { 'x-team-id': teamId },
      });
      setViewingTeamPlayers(data.players);
    } catch (err) { console.error(err); }
    finally { setRosterLoading(false); }
  };

  // Toggle player active/inactive
  const handleTogglePlayer = async (playerId, isActive) => {
    try {
      await apiFetch(`/api/admin/players/${playerId}/${isActive ? 'deactivate' : 'activate'}`, {
        method: 'PUT',
        headers: { 'x-team-id': viewingTeamId },
      });
      loadRoster(viewingTeamId);
      loadDashboard();
    } catch (err) { setError(err.message); }
  };

  // Remove player from club
  const handleRemovePlayer = async (player) => {
    if (!window.confirm(`Remove ${player.first_name} ${player.last_name} from the club? They will be detached from this team. Their parent can reactivate them as an individual account.`)) return;
    try {
      await apiFetch(`/api/club/players/${player.id}/remove`, {
        method: 'POST',
        headers: { 'x-team-id': viewingTeamId },
      });
      loadRoster(viewingTeamId);
      loadDashboard();
      setSuccess(`${player.first_name} ${player.last_name} removed from club.`);
    } catch (err) { setError(err.message); }
  };

  // Load coaches for a team (View Coaches drilldown)
  const loadTeamCoaches = async (teamId) => {
    setCoachesLoading(true);
    try {
      const data = await apiFetch(`/api/club/teams/${teamId}/coaches`);
      setViewingTeamCoaches(data.coaches);
      setViewingTeamInvitations(data.invitations);
    } catch (err) { console.error(err); }
    finally { setCoachesLoading(false); }
  };

  // Update coach info
  const handleUpdateCoach = async (coachId) => {
    setError('');
    setSaving(true);
    try {
      await apiFetch(`/api/club/coaches/${coachId}`, {
        method: 'PUT',
        body: JSON.stringify(editCoachForm),
      });
      setEditingCoachId(null);
      setSuccess('Coach updated.');
      loadTeamCoaches(viewingCoachesTeamId);
      loadCoaches();
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Remove coach from team
  const handleRemoveCoach = async (coachId, coachName) => {
    if (!window.confirm(`Remove ${coachName} from this team?`)) return;
    setError('');
    setSaving(true);
    try {
      await apiFetch(`/api/club/coaches/${coachId}/teams/${viewingCoachesTeamId}`, {
        method: 'DELETE',
      });
      setSuccess('Coach removed from team.');
      loadTeamCoaches(viewingCoachesTeamId);
      loadCoaches();
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Create team
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/teams', {
        method: 'POST',
        body: JSON.stringify(teamForm),
      });
      setShowCreateTeam(false);
      setTeamForm({ name: '', age_group: '', has_under_13: false, coach_ids: [] });
      setSuccess('Team created successfully.');
      await loadDashboard();
      // Auto-open invite coach for the new team
      if (data.team?.id) {
        setInviteForTeamId(data.team.id);
        setInlineInviteEmail('');
      }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Edit team
  const handleEditTeam = async (e) => {
    e.preventDefault();
    if (!editingTeam) return;
    setError('');
    setSaving(true);
    try {
      await apiFetch(`/api/club/teams/${editingTeam.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: teamForm.name, age_group: teamForm.age_group, has_under_13: teamForm.has_under_13 }),
      });
      setEditingTeam(null);
      setTeamForm({ name: '', age_group: '', has_under_13: false, coach_ids: [] });
      setSuccess('Team updated.');
      await loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Invite coach (inline per-team)
  const handleInlineInviteCoach = async (teamId) => {
    if (!inlineInviteEmail.trim()) return;
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/invitations', {
        method: 'POST',
        body: JSON.stringify({ email: inlineInviteEmail, team_id: teamId }),
      });
      setInviteForTeamId(null);
      setInlineInviteEmail('');
      setSuccess(data.linked ? data.message : `Invitation sent to ${inlineInviteEmail}.`);
      loadDashboard();
      loadCoaches();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Move player
  const handleMovePlayer = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch(`/api/club/players/${moveForm.player_id}/move`, {
        method: 'POST',
        body: JSON.stringify({ destination_team_id: moveForm.destination_team_id }),
      });
      setShowMovePlayer(false);
      setMoveForm({ player_id: '', destination_team_id: '' });
      setSuccess(data.message);
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Add player to team (from roster drilldown)
  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { birth_year, ...apRest } = addPlayerForm;
      await apiFetch('/api/admin/players', {
        method: 'POST',
        headers: { 'x-team-id': viewingTeamId },
        body: JSON.stringify({ ...apRest, ...(birth_year ? { birth_year: parseInt(birth_year, 10) } : {}) }),
      });
      setShowAddPlayer(false);
      setAddPlayerForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
      setSuccess('Player added successfully.');
      loadRoster(viewingTeamId);
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Add player from team list (Club Pay)
  const handleAddPlayerFromList = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { birth_year: aplBY, ...aplRest } = addPlayerListForm;
      await apiFetch('/api/admin/players', {
        method: 'POST',
        headers: { 'x-team-id': addPlayerForTeamId },
        body: JSON.stringify({ ...aplRest, ...(aplBY ? { birth_year: parseInt(aplBY, 10) } : {}) }),
      });
      setAddPlayerForTeamId(null);
      setAddPlayerListForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
      setSuccess('Player added successfully.');
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // CSV Import
  const parseCSV = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return [];
    // Skip header if it looks like one
    let start = 0;
    const firstLine = lines[0].toLowerCase();
    if (firstLine.includes('team') && firstLine.includes('name')) start = 1;

    return lines.slice(start).map(line => {
      const cells = line.split(',').map(c => c.trim());
      return {
        team_name: cells[0] || '',
        under_13: cells[1] || '',
        type: cells[2] || '',
        first_name: cells[3] || '',
        last_name: cells[4] || '',
        email: cells[5] || '',
      };
    });
  };

  const handleImportPreview = async () => {
    setError('');
    setImportResult(null);
    const rows = parseCSV(csvText);
    if (rows.length === 0) { setError('No data to import'); return; }
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/import', {
        method: 'POST',
        body: JSON.stringify({ rows, preview: true }),
      });
      setImportPreview(data);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleImportCommit = async () => {
    setError('');
    const rows = parseCSV(csvText);
    setSaving(true);
    try {
      const data = await apiFetch('/api/club/import', {
        method: 'POST',
        body: JSON.stringify({ rows, preview: false }),
      });
      setImportResult(data);
      setImportPreview(null);
      setCsvText('');
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const downloadCredentials = (credentials) => {
    const header = 'Team,First Name,Last Name,Username,Password';
    const csvRows = credentials.map(c =>
      `${c.team_name},${c.first_name},${c.last_name},${c.username},${c.password}`
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'player-credentials.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  // Billing actions
  const handleCheckout = async (plan, interval) => {
    setError('');
    setSaving(true);
    try {
      const bodyObj = { plan, interval };
      const t = localStorage.getItem('trialxyz');
      if (t) bodyObj.trial_days = parseInt(t, 10);
      const data = await apiFetch('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify(bodyObj),
      });
      window.location.href = data.checkout_url;
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleOpenPortal = async () => {
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch('/api/billing/portal', { method: 'POST' });
      window.location.href = data.portal_url;
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleAddPlayers = async (quantity) => {
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/billing/add-players', {
        method: 'POST',
        body: JSON.stringify({ quantity }),
      });
      setSuccess(`Added ${quantity} player${quantity !== 1 ? 's' : ''} to your plan.`);
      loadBilling();
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleCancelSubscription = async (cancelReason) => {
    setShowCancelSurvey(false);
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch('/api/billing/cancel', {
        method: 'POST',
        body: JSON.stringify({ cancel_reason: cancelReason }),
      });
      setSuccess(`Subscription canceled. Access continues until ${new Date(data.access_until).toLocaleDateString()}.`);
      loadBilling();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleReactivateSubscription = async () => {
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/billing/reactivate', { method: 'POST' });
      setSuccess('Subscription reactivated.');
      loadBilling();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Delete team
  const handleDeleteTeam = async (team) => {
    const playerCount = parseInt(team.player_count) || 0;
    const msg = playerCount > 0
      ? `Delete "${team.name}"? This team has ${playerCount} player${playerCount !== 1 ? 's' : ''} who will be deactivated. This cannot be undone.`
      : `Delete "${team.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch(`/api/club/teams/${team.id}`, { method: 'DELETE' });
      setSuccess(data.message);
      loadDashboard();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Download sample import CSV
  const downloadSampleCSV = () => {
    const csv = [
      'Team Name,Under 13?,Coach or Player,First Name,Last Name,Email',
      'U10 Lightning,Yes,Coach,Jane,Smith,jane.smith@example.com',
      'U10 Lightning,Yes,Player,Tommy,Johnson,parent@example.com',
      'U10 Lightning,Yes,Player,Sarah,Williams,',
      'U14 Elite,No,Coach,Mike,Davis,mike.davis@example.com',
      'U14 Elite,No,Player,Alex,Brown,',
      'U14 Elite,No,Player,Jordan,Lee,',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const ageGroups = ['U6','U7','U8','U9','U10','U11','U12','U13','U14','U15','U16','U17','U18','19+'];

  const isParentPays = dashboard?.club?.billing_model === 'parent_pays';
  const isFreeAccount = dashboard?.club?.is_free;
  const navTabs = (isParentPays || isFreeAccount) ? ['teams','reports','import','faq'] : ['teams','reports','import','billing','faq'];
  const tabLabels = { teams: 'Teams', reports: 'Reports', import: 'Import', billing: 'Billing', faq: 'FAQ' };

  // Get the team object for the roster drilldown
  const viewingTeam = dashboard?.teams?.find(t => t.id === viewingTeamId);
  const viewingCoachesTeam = dashboard?.teams?.find(t => t.id === viewingCoachesTeamId);

  if (loading) {
    return (
      <div className="admin-layout">
        <header className="header">
          <div className="header-left">
            <img src={LOGO} alt="Train Daily" className="header-logo" />
            <div className="header-title"><span>Train Daily</span> Club Admin</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-sm btn-outline" onClick={() => navigate('/club/account')}>Manage Account</button>
            <button className="header-logout" onClick={logout}>Log out</button>
          </div>
        </header>
        <div className="admin-page"><div className="loading"><div className="spinner" /></div></div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <header className="header">
        <div className="header-left">
          <img src={LOGO} alt="Train Daily" className="header-logo" />
          <div className="header-title"><span>Train Daily</span> {dashboard?.club?.name || 'Club Admin'}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-sm btn-outline" onClick={() => navigate('/club/account')}>Manage Account</button>
          <button className="header-logout" onClick={logout}>Log out</button>
        </div>
      </header>

      <nav className="admin-nav">
        {navTabs.map(tab => (
          <button key={tab} className={`admin-nav-item ${activeTab === tab ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab); setSuccess(''); setError(''); setViewingTeamId(null); setViewingCoachesTeamId(null); setCsvText(''); setImportPreview(null); setImportResult(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            {tabLabels[tab]}
          </button>
        ))}
      </nav>

      {/* Activation banner for unpaid club_pays clubs */}
      {dashboard?.club?.billing_model === 'club_pays' && !dashboard?.subscription && !isFreeAccount && (
        <div style={{ background: 'rgba(243,156,18,0.15)', padding: '16px 20px', textAlign: 'center', borderBottom: '1px solid rgba(243,156,18,0.3)' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#f39c12', marginBottom: 4 }}>
            Activate Your 30-Day Free Trial
          </div>
          <div style={{ fontSize: '0.85rem', color: '#52627a', marginBottom: 12 }}>
            Enter payment info to activate your account. Cancel anytime before your trial ends and pay nothing.
          </div>
          <div style={{ display: 'flex', gap: 25, justifyContent: 'center' }}>
            <button className="btn btn-orange btn-sm" style={{ width: 'auto', margin: 0 }} onClick={() => handleCheckout('club_pays', 'monthly')} disabled={saving}>
              Activate Monthly ($1.99/player/mo)
            </button>
            <button className="btn btn-primary btn-sm" style={{ width: 'auto', margin: 0 }} onClick={() => handleCheckout('club_pays', 'annual')} disabled={saving}>
              Activate Annual ($1.58/player/mo — Save 20%)
            </button>
          </div>
        </div>
      )}

      {dashboard?.subscription?.status === 'past_due' && (
        <div style={{ background: 'rgba(231,76,60,0.15)', color: '#e74c3c', padding: '10px 16px', textAlign: 'center', fontSize: '0.85rem' }}>
          There's a problem with your payment. Please update your card.
          <button style={{ marginLeft: 8, background: 'none', border: 'none', color: '#e74c3c', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}
            onClick={() => setActiveTab('billing')}>Update now</button>
        </div>
      )}
      {dashboard?.subscription?.status === 'suspended' && (
        <div style={{ background: 'rgba(231,76,60,0.2)', color: '#e74c3c', padding: '12px 16px', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600 }}>
          This account is paused. Please update billing to restore access.
          <button style={{ marginLeft: 8, background: 'none', border: 'none', color: '#e74c3c', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}
            onClick={() => setActiveTab('billing')}>Manage Billing</button>
        </div>
      )}
      {dashboard?.subscription?.status === 'trialing' && dashboard?.subscription?.trial_end && (
        <div style={{ background: 'rgba(52,152,219,0.1)', color: '#3498db', padding: '8px 16px', textAlign: 'center', fontSize: '0.8rem' }}>
          Free Trial - Ends {new Date(dashboard.subscription.trial_end).toLocaleDateString()}
        </div>
      )}

      <div className="admin-page">
        {/* Plan usage */}
        <div className="card" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>Players</strong>
            <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>
              {dashboard?.total_players || 0}
              {dashboard?.club?.player_limit ? ` of ${dashboard.club.player_limit}` : ''} players
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {dashboard?.teams?.length || 0} team{(dashboard?.teams?.length || 0) !== 1 ? 's' : ''}
          </div>
        </div>

        {success && <div style={{ color: 'var(--success)', marginBottom: 12, padding: '10px 14px', background: 'rgba(46,204,113,0.1)', borderRadius: 8 }}>{success}</div>}
        {error && !showCreateTeam && !showAddPlayer && <div style={{ color: '#e74c3c', marginBottom: 12, padding: '10px 14px', background: 'rgba(231,76,60,0.1)', borderRadius: 8 }}>{error}</div>}

        {/* TEAMS TAB */}
        {activeTab === 'teams' && !viewingTeamId && !viewingCoachesTeamId && (
          <>
            <div className="flex-between mb-16">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h2 className="page-title" style={{ marginBottom: 0, fontSize: '1.1rem' }}>Teams</h2>
                <HelpTooltip>
                  <h3>Managing Your Teams</h3>
                  <p>This page lists all the teams in your club. Each card shows the team name, player count, assigned coaches, and active challenge.</p>
                  <p><strong>Create Team</strong> — Add a new team with a name, age group, and assigned coaches.</p>
                  <p><strong>View Players</strong> — See the roster for a team and manage individual players.</p>
                  <p><strong>View Coaches</strong> — See and manage which coaches are assigned to a team.</p>
                  <p><strong>Invite Players</strong> — Get a shareable sign-up link to send to players or parents.{isParentPays ? ' Parents will purchase a subscription when they sign up.' : ''}</p>
                  <p><strong>Invite Coach</strong> — Send an email invitation to add a coach to a team.</p>
                  {!isParentPays && <p><strong>Import Players</strong> — Bulk-add players to a team by pasting CSV data.</p>}
                  <p><strong>Move Player</strong> — Transfer a player from one team to another within your club.</p>
                </HelpTooltip>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => { setShowMovePlayer(true); setMoveForm({ player_id: '', destination_team_id: '' }); setMoveSourceTeam(''); setTeamPlayers([]); }}>Move Player</button>
                <button className="btn btn-outline btn-sm" onClick={() => { setActiveTab('import'); setSuccess(''); setError(''); setViewingTeamId(null); setViewingCoachesTeamId(null); setCsvText(''); setImportPreview(null); setImportResult(null); }}>Import Players</button>
                <button className="btn btn-orange btn-sm" onClick={() => { setShowCreateTeam(true); setTeamForm({ name: '', age_group: '', has_under_13: false, coach_ids: [] }); setError(''); }}>+ Create Team</button>
              </div>
            </div>

            {(!dashboard?.teams || dashboard.teams.length === 0) && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No teams yet. Create your first team.</div>
            )}

            {dashboard?.teams?.map(team => (
              <div key={team.id} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <strong>{team.name}</strong>
                    {team.age_group && <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{team.age_group}</span>}
                    {team.has_under_13 && <span style={{ marginLeft: 8, fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(241,196,15,0.15)', color: '#f1c40f' }}>Under-13</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {team.join_code}
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  {team.player_count} player{parseInt(team.player_count) !== 1 ? 's' : ''}
                  {team.active_challenge_name && <span> &middot; {team.active_challenge_name}</span>}
                </div>
                {team.coaches && team.coaches.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Coach{team.coaches.length !== 1 ? 'es' : ''}: {team.coaches.map(c => `${c.first_name} ${c.last_name}`).join(', ')}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => { setViewingTeamId(team.id); loadRoster(team.id); setSuccess(''); setError(''); }}>View Players</button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setViewingCoachesTeamId(team.id); loadTeamCoaches(team.id); setSuccess(''); setError(''); setEditingCoachId(null); }}>View Coaches</button>
                  {!isParentPays && (
                    <button className="btn btn-orange btn-sm" onClick={() => { setAddPlayerForTeamId(team.id); setAddPlayerListForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '' }); setError(''); }}>Add Player</button>
                  )}
                  <button className="btn btn-blue btn-sm" onClick={() => { setInvitePlayersForTeamId(invitePlayersForTeamId === team.id ? null : team.id); setInvitePlayersCopyMsg(''); }}>Invite Players</button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setInviteForTeamId(inviteForTeamId === team.id ? null : team.id); setInlineInviteEmail(''); setError(''); }}>Invite Coach</button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setEditingTeam(team); setTeamForm({ name: team.name, age_group: team.age_group || '', has_under_13: !!team.has_under_13, coach_ids: [] }); setError(''); }}>Edit Team</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTeam(team)} disabled={saving}>Delete Team</button>
                </div>
                {invitePlayersForTeamId === team.id && (
                  <div style={{ marginTop: 8, background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.2)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                      {isParentPays
                        ? 'Share this link with parents. They\'ll sign up and pay for their player.'
                        : 'Share this link with parents. They\'ll create a free account and their player joins the team.'}
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.2)', padding: '8px 12px', borderRadius: 6, fontSize: '0.8rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 8 }}>
                      {`${window.location.origin}/parents/join/${team.join_code}`}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="btn btn-blue btn-sm" onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/parents/join/${team.join_code}`);
                        setInvitePlayersCopyMsg('Copied!');
                        setTimeout(() => setInvitePlayersCopyMsg(''), 2000);
                      }}>Copy Link</button>
                      {invitePlayersCopyMsg && <span style={{ color: '#2ecc71', fontSize: '0.85em', fontWeight: 600 }}>{invitePlayersCopyMsg}</span>}
                    </div>
                  </div>
                )}
                {inviteForTeamId === team.id && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      className="form-input"
                      type="email"
                      value={inlineInviteEmail}
                      onChange={(e) => setInlineInviteEmail(e.target.value)}
                      placeholder="Enter coach email"
                      autoCapitalize="none"
                      style={{ flex: 1, marginBottom: 0 }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInlineInviteCoach(team.id); } }}
                    />
                    <button className="btn btn-orange btn-sm" onClick={() => handleInlineInviteCoach(team.id)} disabled={saving || !inlineInviteEmail.trim()}>
                      {saving ? 'Inviting...' : 'Invite'}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Pending invitations */}
            {dashboard?.invitations?.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h3 style={{ fontSize: '0.95rem', marginBottom: 8 }}>Pending Invitations</h3>
                {dashboard.invitations.map(inv => (
                  <div key={inv.id} className="card" style={{ marginBottom: 6, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span>{inv.email}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>{inv.role}</span>
                      {inv.team_name && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>&middot; {inv.team_name}</span>}
                    </div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--orange)' }}>pending</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* TEAMS TAB - Roster Drilldown */}
        {activeTab === 'teams' && viewingTeamId && (
          <>
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setViewingTeamId(null); setViewingTeamPlayers([]); setShowAddPlayer(false); setShowInvitePlayers(false); setSuccess(''); setError(''); }}>
                <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i> Back to Teams
              </button>
            </div>
            <div className="flex-between mb-16">
              <h2 className="page-title" style={{ marginBottom: 0, fontSize: '1.1rem' }}>{viewingTeam?.name || 'Team'} Roster</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-blue btn-sm" onClick={() => { setShowInvitePlayers(true); setInviteCopyMsg(''); }}>Invite Players</button>
                {!isParentPays && (
                  <button className="btn btn-orange btn-sm" onClick={() => { setShowAddPlayer(true); setAddPlayerForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '' }); setError(''); }}>Add Player</button>
                )}
              </div>
            </div>

            {rosterLoading && <div className="loading"><div className="spinner" /></div>}

            {!rosterLoading && viewingTeamPlayers.length === 0 && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No players on this team yet.</div>
            )}

            {!rosterLoading && viewingTeamPlayers.map(p => (
              <div key={p.id} className="card" style={{ marginBottom: 6, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div>
                    <strong>{p.first_name} {p.last_name}</strong>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>@{p.username}</span>
                    {p.status === 'inactive' && <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, marginLeft: 8, background: 'rgba(108,108,108,0.15)', color: 'var(--text-muted)' }}>Inactive</span>}
                    {p.consent_status === 'awaiting' && <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, marginLeft: 8, background: '#fff3cd', color: '#856404' }}>Awaiting Consent</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {p.parent_email || ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-sm btn-outline" onClick={() => navigate(`/club/players/${p.id}/edit?team=${viewingTeamId}`)}>Edit</button>
                  <button className="btn btn-sm btn-outline" onClick={() => handleTogglePlayer(p.id, p.status === 'active')}>
                    {p.status === 'active' ? 'Deactivate' : 'Reactivate'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleRemovePlayer(p)}>Remove from Club</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* TEAMS TAB - Coaches Drilldown */}
        {activeTab === 'teams' && viewingCoachesTeamId && (
          <>
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setViewingCoachesTeamId(null); setViewingTeamCoaches([]); setViewingTeamInvitations([]); setEditingCoachId(null); setSuccess(''); setError(''); }}>
                &larr; Back to Teams
              </button>
            </div>
            <h2 className="page-title" style={{ marginBottom: 16, fontSize: '1.1rem' }}>{viewingCoachesTeam?.name || 'Team'} Coaches</h2>

            {coachesLoading && <div className="loading"><div className="spinner" /></div>}

            {!coachesLoading && viewingTeamCoaches.length === 0 && viewingTeamInvitations.length === 0 && (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No coaches on this team yet.</div>
            )}

            {!coachesLoading && viewingTeamCoaches.map(c => (
              <div key={c.id} className="card" style={{ marginBottom: 8, padding: '12px 14px' }}>
                {editingCoachId === c.id ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input className="form-input" value={editCoachForm.first_name} onChange={(e) => setEditCoachForm({...editCoachForm, first_name: e.target.value})} placeholder="First name" style={{ flex: 1, marginBottom: 0 }} />
                      <input className="form-input" value={editCoachForm.last_name} onChange={(e) => setEditCoachForm({...editCoachForm, last_name: e.target.value})} placeholder="Last name" style={{ flex: 1, marginBottom: 0 }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <input className="form-input" type="email" value={editCoachForm.email} onChange={(e) => setEditCoachForm({...editCoachForm, email: e.target.value})} placeholder="Email" autoCapitalize="none" style={{ marginBottom: 0 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-orange btn-sm" onClick={() => handleUpdateCoach(c.id)} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                      <button className="btn btn-outline btn-sm" onClick={() => setEditingCoachId(null)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ fontSize: '0.9rem' }}>{c.first_name} {c.last_name}</strong>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>{c.email}</span>
                      <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, marginLeft: 8, background: 'rgba(46,204,113,0.15)', color: '#2ecc71', fontWeight: 600 }}>Active</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => { setEditingCoachId(c.id); setEditCoachForm({ first_name: c.first_name, last_name: c.last_name, email: c.email }); setError(''); }}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleRemoveCoach(c.id, `${c.first_name} ${c.last_name}`)} disabled={saving}>Remove</button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {!coachesLoading && viewingTeamInvitations.map(inv => (
              <div key={inv.id} className="card" style={{ marginBottom: 8, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '0.9rem' }}>{inv.email}</span>
                  <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, marginLeft: 8, background: 'rgba(243,156,18,0.15)', color: '#f39c12', fontWeight: 600 }}>Pending</span>
                </div>
              </div>
            ))}
          </>
        )}

        {/* REPORTS TAB */}
        {activeTab === 'reports' && (
          <>
            <h2 className="page-title" style={{ fontSize: '1.1rem' }}>Club Reports</h2>
            {!clubReports && !reportsLoading && (
              <button className="btn btn-orange btn-sm" onClick={loadClubReports}>Load Reports</button>
            )}
            {reportsLoading && <div className="loading"><div className="spinner" /></div>}
            {clubReports && (
              <>
                {/* Club-wide summary */}
                <div className="card" style={{ marginBottom: 12, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>This Week</div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>
                        <span style={{ color: 'var(--orange)' }}>{clubReports.active_this_week}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--text-muted)' }}> of {clubReports.total_players} active</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Completion Rate</div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--orange)' }}>{clubReports.club_completion_rate}%</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
                    {clubReports.total_teams} team{clubReports.total_teams !== 1 ? 's' : ''} &middot; {clubReports.total_players} players
                  </div>
                </div>

                {/* Dormant teams warning */}
                {clubReports.team_stats.some(t => t.dormant) && (
                  <div style={{ background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '0.82rem' }}>
                    <div style={{ fontWeight: 700, color: '#f39c12', marginBottom: 4 }}>Dormant Teams</div>
                    <div style={{ color: 'var(--text-muted)' }}>
                      No activity in 10+ days: {clubReports.team_stats.filter(t => t.dormant).map(t => t.name).join(', ')}
                    </div>
                  </div>
                )}

                {/* Weekly trend */}
                {(() => {
                  const maxW = Math.max(...clubReports.weekly_trend.map(w => w.completions), 1);
                  return (
                    <div className="card" style={{ marginBottom: 12, padding: '14px 16px' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 10 }}>Weekly Completions (Club-wide)</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
                        {clubReports.weekly_trend.map((w, i) => {
                          const fmtDate = (ds) => { const d = new Date((ds||'').split('T')[0] + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{w.completions || ''}</div>
                              <div style={{ width: '100%', background: i === clubReports.weekly_trend.length - 1 ? 'var(--orange)' : 'var(--card-border)', borderRadius: 3, height: `${Math.max((w.completions / maxW) * 60, 2)}px` }} />
                              <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>{fmtDate(w.week_start)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Per-team stats */}
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Teams</div>
                {clubReports.team_stats.map(t => (
                  <div key={t.id} className="card" style={{ marginBottom: 6, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{t.name}</span>
                        {t.dormant && <span style={{ marginLeft: 8, fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(243,156,18,0.15)', color: '#f39c12' }}>Dormant</span>}
                        {!t.has_season && <span style={{ marginLeft: 8, fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(108,108,108,0.15)', color: 'var(--text-muted)' }}>No challenge</span>}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {t.last_activity ? (() => {
                          const d = new Date(t.last_activity);
                          const diff = Math.floor((new Date() - d) / (1000*60*60*24));
                          return diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : `${diff}d ago`;
                        })() : 'Never'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      <span>{t.player_count} players</span>
                      <span>{t.active_percent}% active this week</span>
                      <span>{t.completion_rate}% completion rate</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* IMPORT TAB */}
        {activeTab === 'import' && (
          <>
            <h2 className="page-title" style={{ fontSize: '1.1rem' }}>Bulk Team Import</h2>
            <div className="card" style={{ marginBottom: 16 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                Upload a CSV to create teams, coaches, and players in one pass. Columns (comma-separated):
              </p>
              <code style={{ display: 'block', fontSize: '0.8rem', background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 6, marginBottom: 12, color: 'var(--orange)' }}>
                Team Name, Under 13?, Coach or Player, First Name, Last Name, Email
              </code>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                Email is required for coaches. For players on under-13 teams, email is the parent email for consent. A header row is optional.
              </p>
              <div style={{ marginBottom: 12 }}>
                <button className="btn btn-orange btn-sm" onClick={downloadSampleCSV} style={{ marginBottom: 8 }}>Download Sample CSV</button>
              </div>
              <div style={{ marginBottom: 12 }}>
                <input type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ marginBottom: 8 }} />
              </div>
              <textarea
                className="form-input"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="Or paste CSV data here..."
                rows={8}
                style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
              />
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button className="btn btn-blue btn-sm" onClick={handleImportPreview} disabled={saving || !csvText.trim()}>
                  {saving ? 'Validating...' : 'Preview Import'}
                </button>
                {importPreview && importPreview.summary.errors.length === 0 && (
                  <button className="btn btn-orange btn-sm" onClick={handleImportCommit} disabled={saving}>
                    {saving ? 'Importing...' : 'Commit Import'}
                  </button>
                )}
              </div>
            </div>

            {importPreview && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '0.95rem', marginBottom: 8 }}>Preview</h3>
                <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>
                  <div>Teams to create: <strong>{importPreview.summary.teams_to_create}</strong></div>
                  <div>Existing teams: <strong>{importPreview.summary.teams_existing}</strong></div>
                  <div>Coaches: <strong>{importPreview.summary.coaches}</strong></div>
                  <div>Players: <strong>{importPreview.summary.players}</strong></div>
                  {importPreview.summary.duplicates_skipped > 0 && (
                    <div style={{ color: '#6c757d' }}>Existing players skipped: <strong>{importPreview.summary.duplicates_skipped}</strong></div>
                  )}
                </div>
                {importPreview.errors.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {importPreview.errors.map((err, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: err.error ? '#e74c3c' : '#f39c12', marginBottom: 4 }}>
                        Row {err.row}: {err.error || err.warning}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {importResult && (
              <div className="card">
                <h3 style={{ fontSize: '0.95rem', marginBottom: 8, color: 'var(--success)' }}>Import Complete</h3>
                <div style={{ fontSize: '0.85rem', marginBottom: 12 }}>
                  <div>Teams created: <strong>{importResult.summary.teams_created}</strong></div>
                  <div>Coaches processed: <strong>{importResult.summary.coaches_processed}</strong></div>
                  <div>Players created: <strong>{importResult.summary.players_created}</strong></div>
                </div>
                {importResult.credentials.length > 0 && (
                  <button className="btn btn-blue btn-sm" onClick={() => downloadCredentials(importResult.credentials)}>
                    Download Player Logins
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* BILLING TAB */}
        {activeTab === 'billing' && !isParentPays && (
          <div>
            <h2 className="page-title" style={{ fontSize: '1.1rem' }}>Billing</h2>
            {billingLoading && <div className="loading"><div className="spinner" /></div>}

            {!billingLoading && billingData?.subscription && (
              <>
                {/* Past due warning */}
                {billingData.subscription.status === 'past_due' && (
                  <div className="billing-past-due-banner">
                    Payment failed. Please update your payment method to avoid service interruption.
                    <button className="btn btn-sm btn-outline" onClick={handleOpenPortal} disabled={saving} style={{ marginLeft: 8 }}>
                      Update Payment
                    </button>
                  </div>
                )}

                {/* Canceled notice */}
                {billingData.subscription.canceled_at && (
                  <div className="billing-canceled-notice">
                    Your subscription will end on {new Date(billingData.subscription.current_period_end).toLocaleDateString()}.
                    <button className="btn btn-sm btn-outline" onClick={handleReactivateSubscription} disabled={saving} style={{ marginLeft: 8 }}>
                      Reactivate
                    </button>
                  </div>
                )}

                {/* Current plan card */}
                <div className="card" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ fontSize: '1rem' }}>
                        {billingData.subscription.plan.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </strong>
                      <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        ({billingData.subscription.billing_interval})
                      </span>
                    </div>
                    <div className="billing-status-badge" data-status={billingData.subscription.status}>
                      {billingData.subscription.status === 'trialing' && billingData.subscription.trial_end
                        ? `Free Trial - Ends ${new Date(billingData.subscription.trial_end).toLocaleDateString()}`
                        : billingData.subscription.status}
                    </div>
                  </div>
                  {billingData.subscription.per_player && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 8 }}>
                      {billingData.subscription.per_player_price} per player / {billingData.subscription.billing_interval === 'annual' ? 'year' : 'month'}
                    </div>
                  )}
                  {billingData.subscription.current_period_end && billingData.subscription.status !== 'trialing' && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
                      Next billing date: {new Date(billingData.subscription.current_period_end).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Roster / Usage */}
                <div className="card" style={{ marginBottom: 12 }}>
                  {billingData.subscription.per_player ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>Active Roster</strong>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          {billingData.subscription.roster_count} player{billingData.subscription.roster_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 6 }}>
                        Estimated next bill: <strong style={{ color: 'var(--orange)' }}>{billingData.subscription.estimated_next_bill}</strong>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        Roster changes are synced daily. Add or remove players anytime.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>Player Usage</strong>
                        <span style={{ fontSize: '0.85rem', color: billingData.usage.players > billingData.usage.cap ? '#e74c3c' : 'var(--text-muted)' }}>
                          {billingData.usage.players} of {billingData.usage.cap} players
                        </span>
                      </div>
                      <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.min(100, (billingData.usage.players / billingData.usage.cap) * 100)}%`,
                          height: '100%',
                          background: billingData.usage.players >= billingData.usage.cap ? '#e74c3c' : 'var(--orange)',
                          borderRadius: 4,
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      {billingData.subscription.addon_quantity > 0 && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
                          Includes {billingData.subscription.addon_quantity} add-on player{billingData.subscription.addon_quantity !== 1 ? 's' : ''}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Payment method */}
                {billingData.subscription.card_last4 && (
                  <div className="card" style={{ marginBottom: 12 }}>
                    <strong>Payment Method</strong>
                    <div style={{ marginTop: 4, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                      {billingData.subscription.card_brand && billingData.subscription.card_brand.charAt(0).toUpperCase() + billingData.subscription.card_brand.slice(1)} ending in {billingData.subscription.card_last4}
                      <span style={{ marginLeft: 8 }}>
                        (expires {billingData.subscription.card_exp_month}/{billingData.subscription.card_exp_year})
                      </span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={handleOpenPortal} disabled={saving}>
                    Manage Payment
                  </button>
                  {!billingData.subscription.per_player && (
                    <>
                      <button className="btn btn-outline btn-sm" onClick={() => handleAddPlayers(5)} disabled={saving}>
                        + Add 5 Players
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => handleAddPlayers(10)} disabled={saving}>
                        + Add 10 Players
                      </button>
                    </>
                  )}
                  {!billingData.subscription.canceled_at && (
                    <button className="btn btn-outline btn-sm" onClick={() => setShowCancelSurvey(true)} disabled={saving} style={{ color: '#e74c3c' }}>
                      Cancel
                    </button>
                  )}
                </div>
              </>
            )}

            {/* No subscription - show activation for club_pays or plan selection for others */}
            {!billingLoading && !billingData?.subscription && (
              <>
                {dashboard?.club?.billing_model === 'club_pays' ? (
                  <>
                    <div className="card" style={{ padding: '20px', textAlign: 'center', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>Activate Your 30-Day Free Trial</div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 16 }}>
                        Per-player pricing — only pay for the players on your roster. Cancel anytime before your trial ends and pay nothing.
                      </p>
                      <div style={{ display: 'grid', gap: 12, maxWidth: 400, margin: '0 auto' }}>
                        <div className="card" style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div><strong>Monthly</strong></div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>$1.99</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>per player / month</div>
                            </div>
                          </div>
                          <button className="btn btn-orange btn-sm" onClick={() => handleCheckout('club_pays', 'monthly')} disabled={saving} style={{ width: '100%', marginTop: 10 }}>
                            Activate Monthly
                          </button>
                        </div>
                        <div className="card" style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong>Annual</strong>
                              <span style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--orange)' }}>Save 20%</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>$1.58</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>per player / month ($19/yr)</div>
                            </div>
                          </div>
                          <button className="btn btn-outline btn-sm" onClick={() => handleCheckout('club_pays', 'annual')} disabled={saving} style={{ width: '100%', marginTop: 10 }}>
                            Activate Annual
                          </button>
                        </div>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                      Roster changes are trued up monthly, so you only ever pay for the players you have.
                    </p>
                  </>
                ) : billingPlans && (
                  <>
                    <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: '0.9rem' }}>
                      Choose a plan to get started with a 30-day free trial.
                    </p>
                    <div style={{ display: 'grid', gap: 12 }}>
                      {billingPlans.plans.map(plan => (
                        <div key={plan.id} className="card" style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong>{plan.name}</strong>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                Up to {plan.player_cap} players
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{plan.monthly_price}/mo</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>or {plan.annual_price}/yr</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button className="btn btn-orange btn-sm" onClick={() => handleCheckout(plan.id, 'monthly')} disabled={saving}>
                              Monthly
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => handleCheckout(plan.id, 'annual')} disabled={saving}>
                              Annual (save {Math.round((1 - (parseFloat(plan.annual_price.replace(/[$,]/g, '')) / (parseFloat(plan.monthly_price.replace(/[$,]/g, '')) * 12))) * 100)}%)
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 16, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Need more players? Add-on players available for {billingPlans.addon.per_player_monthly}/mo or {billingPlans.addon.per_player_annual}/yr each.
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* FAQ TAB */}
        {activeTab === 'faq' && <Faq audience="club" />}

        {/* CREATE TEAM MODAL */}
        {showCreateTeam && (
          <div className="modal-overlay" onClick={() => setShowCreateTeam(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Create Team</h2>
              {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
              <form onSubmit={handleCreateTeam}>
                <div className="form-group">
                  <label className="form-label">Team Name</label>
                  <input className="form-input" value={teamForm.name} onChange={(e) => setTeamForm({...teamForm, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Age Group</label>
                  <select className="form-input" value={teamForm.age_group} onChange={(e) => setTeamForm({...teamForm, age_group: e.target.value})} required>
                    <option value="">Select age group</option>
                    {ageGroups.map(ag => <option key={ag} value={ag}>{ag}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={teamForm.has_under_13} onChange={(e) => setTeamForm({...teamForm, has_under_13: e.target.checked})} />
                    <span className="form-label" style={{ marginBottom: 0 }}>Are there any players under the age of 13 on this team?</span>
                  </label>
                </div>
                {coaches.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Assign Coaches</label>
                    {coaches.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={teamForm.coach_ids.includes(c.id)}
                          onChange={(e) => {
                            setTeamForm({...teamForm,
                              coach_ids: e.target.checked
                                ? [...teamForm.coach_ids, c.id]
                                : teamForm.coach_ids.filter(id => id !== c.id)
                            });
                          }}
                        />
                        <span style={{ fontSize: '0.9rem' }}>{c.first_name} {c.last_name} ({c.email})</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowCreateTeam(false)}>Cancel</button>
                  <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Creating...' : 'Create Team'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT TEAM MODAL */}
        {editingTeam && (
          <div className="modal-overlay" onClick={() => setEditingTeam(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Edit Team</h2>
              {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
              <form onSubmit={handleEditTeam}>
                <div className="form-group">
                  <label className="form-label">Team Name</label>
                  <input className="form-input" value={teamForm.name} onChange={(e) => setTeamForm({...teamForm, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Age Group</label>
                  <select className="form-input" value={teamForm.age_group} onChange={(e) => setTeamForm({...teamForm, age_group: e.target.value})} required>
                    <option value="">Select age group</option>
                    {ageGroups.map(ag => <option key={ag} value={ag}>{ag}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={teamForm.has_under_13} onChange={(e) => setTeamForm({...teamForm, has_under_13: e.target.checked})} />
                    <span className="form-label" style={{ marginBottom: 0 }}>Are there any players under the age of 13 on this team?</span>
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setEditingTeam(null)}>Cancel</button>
                  <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MOVE PLAYER MODAL */}
        {showMovePlayer && (
          <div className="modal-overlay" onClick={() => setShowMovePlayer(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Move Player</h2>
              {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
              <form onSubmit={handleMovePlayer}>
                <div className="form-group">
                  <label className="form-label">From Team</label>
                  <select className="form-input" value={moveSourceTeam} onChange={(e) => { loadTeamPlayers(e.target.value); setMoveForm({...moveForm, player_id: ''}); }}>
                    <option value="">Select source team</option>
                    {dashboard?.teams?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                {moveSourceTeam && (
                  <div className="form-group">
                    <label className="form-label">Player</label>
                    <select className="form-input" value={moveForm.player_id} onChange={(e) => setMoveForm({...moveForm, player_id: e.target.value})} required>
                      <option value="">Select player</option>
                      {teamPlayers.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.username})</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">To Team</label>
                  <select className="form-input" value={moveForm.destination_team_id} onChange={(e) => setMoveForm({...moveForm, destination_team_id: e.target.value})} required>
                    <option value="">Select destination team</option>
                    {dashboard?.teams?.filter(t => t.id !== moveSourceTeam).map(t => <option key={t.id} value={t.id}>{t.name}{t.age_group ? ` (${t.age_group})` : ''}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                  The player keeps their lifetime points. Challenge stats reset on the new team. If the destination team is under-13 and the player hasn't consented, they'll need parental consent.
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowMovePlayer(false)}>Cancel</button>
                  <button type="submit" className="btn btn-orange" disabled={saving || !moveForm.player_id || !moveForm.destination_team_id}>{saving ? 'Moving...' : 'Move Player'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ADD PLAYER MODAL */}
        {showAddPlayer && (
          <div className="modal-overlay" onClick={() => setShowAddPlayer(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Add Player to {viewingTeam?.name}</h2>
              {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
              <form onSubmit={handleAddPlayer}>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="form-input" value={addPlayerForm.first_name} onChange={(e) => setAddPlayerForm({...addPlayerForm, first_name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="form-input" value={addPlayerForm.last_name} onChange={(e) => setAddPlayerForm({...addPlayerForm, last_name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Birth Year <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <select className="form-input" value={addPlayerForm.birth_year} onChange={(e) => setAddPlayerForm({...addPlayerForm, birth_year: e.target.value})}>
                    <option value="">--</option>
                    {Array.from({ length: 23 }, (_, i) => { const y = new Date().getFullYear() - 3 - i; return <option key={y} value={y}>{y}</option>; })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input className="form-input" value={addPlayerForm.username} onChange={(e) => setAddPlayerForm({...addPlayerForm, username: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="text" value={addPlayerForm.password} onChange={(e) => setAddPlayerForm({...addPlayerForm, password: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Parent Email</label>
                  <input className="form-input" type="email" value={addPlayerForm.parent_email} onChange={(e) => setAddPlayerForm({...addPlayerForm, parent_email: e.target.value})} autoCapitalize="none" />
                  {viewingTeam?.has_under_13 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Required for under-13 consent</div>
                  )}
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowAddPlayer(false)}>Cancel</button>
                  <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Adding...' : 'Add Player'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ADD PLAYER FROM TEAM LIST MODAL (Club Pay) */}
        {addPlayerForTeamId && (
          <div className="modal-overlay" onClick={() => setAddPlayerForTeamId(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Add Player to {dashboard?.teams?.find(t => t.id === addPlayerForTeamId)?.name}</h2>
              {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
              <form onSubmit={handleAddPlayerFromList}>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="form-input" value={addPlayerListForm.first_name} onChange={(e) => setAddPlayerListForm({...addPlayerListForm, first_name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="form-input" value={addPlayerListForm.last_name} onChange={(e) => setAddPlayerListForm({...addPlayerListForm, last_name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Birth Year <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <select className="form-input" value={addPlayerListForm.birth_year} onChange={(e) => setAddPlayerListForm({...addPlayerListForm, birth_year: e.target.value})}>
                    <option value="">--</option>
                    {Array.from({ length: 23 }, (_, i) => { const y = new Date().getFullYear() - 3 - i; return <option key={y} value={y}>{y}</option>; })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input className="form-input" value={addPlayerListForm.username} onChange={(e) => setAddPlayerListForm({...addPlayerListForm, username: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="text" value={addPlayerListForm.password} onChange={(e) => setAddPlayerListForm({...addPlayerListForm, password: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Parent Email</label>
                  <input className="form-input" type="email" value={addPlayerListForm.parent_email} onChange={(e) => setAddPlayerListForm({...addPlayerListForm, parent_email: e.target.value})} autoCapitalize="none" />
                  {dashboard?.teams?.find(t => t.id === addPlayerForTeamId)?.has_under_13 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Required for under-13 consent</div>
                  )}
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setAddPlayerForTeamId(null)}>Cancel</button>
                  <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Adding...' : 'Add Player'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* INVITE PLAYERS MODAL (Parent Signup Link) */}
        {showInvitePlayers && viewingTeam && (
          <div className="modal-overlay" onClick={() => setShowInvitePlayers(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ color: '#000000' }}>
              <h2>Invite Players</h2>
              <p style={{ fontSize: '0.9em', color: '#666', marginBottom: 16 }}>
                Share this link with parents so they can sign up their player and join <strong>{viewingTeam.name}</strong>.
              </p>
              <div style={{ background: '#f5f5f5', padding: '12px 16px', borderRadius: 8, fontSize: '0.85rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 12 }}>
                {`${window.location.origin}/parents/join/${viewingTeam.join_code}`}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-blue btn-sm" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/parents/join/${viewingTeam.join_code}`);
                  setInviteCopyMsg('Copied!');
                  setTimeout(() => setInviteCopyMsg(''), 2000);
                }}>
                  Copy Link
                </button>
                {inviteCopyMsg && <span style={{ color: '#2ecc71', fontSize: '0.85em', fontWeight: 600 }}>{inviteCopyMsg}</span>}
              </div>
              <p style={{ fontSize: '0.8em', color: '#999', marginTop: 16 }}>
                Parents will create an account and add their player to your team.
              </p>
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button className="btn btn-outline btn-sm" onClick={() => setShowInvitePlayers(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
        <div style={{ textAlign: 'center', padding: '24px 0 12px', fontSize: '0.85rem' }}>
          <a href="/support" style={{ color: 'var(--text-muted)' }}>Support</a>
        </div>
      </div>

      {showCancelSurvey && (
        <CancelSurveyModal
          onConfirm={handleCancelSubscription}
          onClose={() => setShowCancelSurvey(false)}
        />
      )}
    </div>
  );
}
